import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import { LayoutAside, LayoutBody, LayoutContent, LayoutContentTitle } from 'shared/layout';
import { booksQuery, tagsQuery } from "shared/queries";
import { useMedia, useNavigate, useStore, useUrlParams, useUserWidgets } from "shared/hooks";
import { BookModel, TagModel } from "shared/models";
import { UiDataBoundary, UiFilter, UiGrid, UiPagination, UiTabs, UiViewType } from "shared/uikit";
import { ApplicationModule } from "shared/modules";
import { ROUTES } from "shared/constants";
import { BookTypeEnum } from "shared/enums";

import { BookItemRow } from "./components/BookItemRow";
import { BookItemCard } from "./components/BookItemCard";

import './styles.scss';

export const BooksPage = observer(() => {
    const navigate = useNavigate();
    const urlParams = useUrlParams({
        page: 1,
        id: undefined as undefined | number,
        typeId: BookTypeEnum.Ebook as string,
        tagId: null as null | string,
    });

    const store = useStore(() => ({
        isLoading: true,
        isSuccess: false,
        pages: 1,
        viewType: 'card',
        books: [] as BookModel[],
        tags: [] as TagModel[],
    }));

    const { isMobile, value: columns } = useMedia({
        is320: 1,
        is1024: 3,
        is1440: 4,
        is2560: 6
    });

    useEffect(() => {
        ApplicationModule.setTitle('Книги');
        (async () => {
            const response = await tagsQuery({
                has: ['books']
            });
            if (response.isSuccess && response.data) {
                store.set('tags', response.data.items.map(item => new TagModel(item)));
            } else {
                store.set('isSuccess', false);
            }
        })();
    }, [store]);

    useEffect(() => {
        (async () => {
            store.set('isLoading', true);
            store.set('isSuccess', true);
            const { data, isSuccess } = await booksQuery({
                ...urlParams,
            });
            if (isSuccess && data) {
                store.set('books', data.items.map(item => new BookModel(item)));
                store.set('pages', data.pages);
            } else {
                store.set('isSuccess', false);
            }
            store.set('isLoading', false);
        })();
    }, [store, urlParams]);

    return (
        <LayoutBody>
            <LayoutContent>
                <LayoutContentTitle title='Книги' backTo={ROUTES.LIBRARY()}>
                    <UiFilter
                        value={urlParams.tagId}
                        items={store.tags}
                        onChange={({ value }) => {
                            navigate(ROUTES.BOOKS(), { tagId: value }, { replace: true })
                        }}
                    />
                    <UiViewType value={store.viewType} onChange={({ value }) => store.set("viewType", value)}/>
                </LayoutContentTitle>
                <div className="p-books__tabs">
                    <UiTabs
                        items={BookTypeEnum.items}
                        value={urlParams.typeId}
                        onChange={({ value }) => {
                            navigate(ROUTES.BOOKS(), {
                                ...urlParams,
                                typeId: value
                            }, { replace: true })
                        }}
                    />
                </div>
                <div className="p-books__description">
                    {urlParams.typeId === BookTypeEnum.Ebook && 'В этом разделе собраны электронные версии книг, доступные для скачивания.'}
                    {urlParams.typeId === BookTypeEnum.Book && (
                        <>
                            <p>Печатные книги можно получить в центральном офисе в Красногорске (4-й этаж, кабинет HR).</p>
                            <p>Ответственная — Родионова Анна (доб. 6544). Книгу можно взять на месяц, при необходимости продлить.</p>
                            <p>Кстати, если у вас есть книги, которые уже прочитали и не планируете хранить — приносите в библиотеку, пополним нашу коллекцию вместе!</p>
                        </>
                    )}
                </div>
                <UiDataBoundary isLoading={store.isLoading} isError={!store.isSuccess}>
                    {(store.viewType === 'list' && !isMobile) &&
                        store.books.map(book => <BookItemRow key={book.id} book={book}/>)
                    }
                    {(store.viewType === 'card' || isMobile) && (
                        <UiGrid columns={columns} gap={[0, 40]}>
                            {store.books.map(book => <BookItemCard key={book.id} book={book}/>)}
                        </UiGrid>
                    )}
                    {store.books.length === 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <h2 className='typography'>По вашему запросу ничего не найдено</h2>
                        </div>
                    )}
                </UiDataBoundary>
                <UiPagination
                    page={urlParams.page}
                    pages={store.pages}
                    onChange={(data) => {
                        navigate(ROUTES.BOOKS(), {
                            ...urlParams,
                            page: data.value
                        }, { replace: true })
                    }}
                />
            </LayoutContent>
            <LayoutAside widgets={useUserWidgets()}/>
        </LayoutBody>

    )
});
